/**
 * @file button.component.tsx
 * @path packages/ui/src/button.component.tsx
 * @description
 */

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@sirbastians/utils";

const buttonVariants = cva(
  "overflow-hidden inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border",
  {
    variants: {
      variant: {
        // 1. Primary (Dark Gray Base)
        // Solid border matching the background to maintain size consistency
        primary:
          "bg-primary text-primary-foreground border-primary neuro-bezel",

        // 2. Secondary (Light Gray Base)
        // Uses 'border-input' to define the edge against white backgrounds (matches Checkbox logic)
        secondary:
          "bg-secondary text-secondary-foreground border-input neuro-bezel",

        // 3. Accent (Green Base)
        // Solid border matching the accent color
        accent: "bg-accent text-accent-foreground border-accent neuro-bezel",

        // 4. Ghost (Flat)
        // Transparent border to prevent layout shifts when hovering
        ghost: "border-transparent hover:bg-secondary/50 hover:text-foreground",

        // 5. Link (Text only)
        link: "text-primary underline-offset-4 hover:text-accent hover:underline border-transparent",

        // 6. Destructive (Red Base)
        destructive:
          "bg-destructive text-destructive-foreground border-destructive neuro-bezel",

        // 7. Outline (Added)
        outline:
          "border border-input bg-transparent text-foreground hover:bg-secondary/50 hover:text-foreground neuro-bezel",

        // 8. Glass (Added)
        glass: cn(
          "bg-nav-bg/20 backdrop-blur-md border-nav-border/10 text-nav-text",
          "active:scale-95 neuro-bezel",
        ),
      },
      size: {
        default: "h-11 px-6 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-12 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
